import { fetchBuyClicksCampaignLocationsMockData } from 'helpers/mocks';
import { sleep } from 'helpers/common';
import { rootApi } from './rootApi';

rootApi.enhanceEndpoints({
  addTagTypes: [
    'BuyClicksCampaign',
    'BuyClicksCategoriesStats',
    'BuyClicksCampaignLocations',
    'DeliveryUrlValid',
    'BuyCampaignsStats',
    'HasBuyClicksCampaigns',
  ],
});

export const buyClicksCampaignApi = rootApi.injectEndpoints({
  endpoints(build) {
    return {
      /**
       * Campaign
       */
      getHasBuyClicksCampaigns: build.query({
        query: (params) => ({
          url: '/ClickBuyCampaign/',
          method: 'get',
          params: {
            page_size: 1,
            archive_mode: 'all',
            ...params,
          },
        }),
        keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
        providesTags: () => {
          return ['HasBuyClicksCampaigns'];
        },
        transformResponse: ({ count } = {}) => {
          return !!count;
        },
      }),

      getBuyClicksCampaigns: build.query({
        query: (params) => ({
          url: '/ClickBuyCampaign/',
          method: 'get',
          params,
        }),
        providesTags: () => {
          return ['BuyClicksCampaign'];
        },
      }),
      getBuyClicksCampaign: build.query({
        query: ({ id, ...params } = {}) => ({
          url: `/ClickBuyCampaign/${id}/`,
          method: 'get',
          params: { archive_mode: 'all', ...params },
        }),
        providesTags: (props) => {
          const { id } = props || {};

          return [{ type: 'BuyClicksCampaign', id }];
        },
        transformResponse: (response) => {
          if (!response) return response;

          const res = { ...response };
          res.vertical = res.taxonomy.vertical;

          return res;
        },
      }),
      createBuyClicksCampaign: build.mutation({
        query: ({ ...data } = {}) => ({
          url: '/ClickBuyCampaign/',
          method: 'post',
          data,
        }),
        invalidatesTags: () => {
          return [
            { type: 'BuyClicksCampaign' },
            { type: 'BuyCampaignsStats' },
            { type: 'BuyCampaignsGroupsStats' },
            { type: 'HasBuyClicksCampaigns' },
          ];
        },
      }),
      putBuyClicksCampaign: build.mutation({
        query: ({ id, ...data } = {}) => ({
          url: `/ClickBuyCampaign/${id}/`,
          method: 'put',
          data,
        }),
        invalidatesTags: () => {
          return ['BuyCampaignsStats', 'BuyClicksCampaign'];
        },
      }),
      patchBuyClicksCampaign: build.mutation({
        query: ({ id, archive_mode = 'all', ...data } = {}) => ({
          url: `/ClickBuyCampaign/${id}/`,
          method: 'patch',
          data,
          params: { archive_mode },
        }),
        invalidatesTags: () => {
          return ['BuyCampaignsStats', 'BuyClicksCampaign'];
        },
      }),
      deleteBuyClicksCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids, ...params } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/ClickBuyCampaign/${id}/`,
                  method: 'delete',
                  params,
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return [
            'BuyCampaignsStats',
            'BuyCampaignsGroupsStats',
            'HasBuyClicksCampaigns',
          ];
        },
      }),
      archiveBuyClicksCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/ClickBuyCampaign/${id}/archive/`,
                  method: 'post',
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return [
            'BuyCampaignsStats',
            'BuyCampaignsGroupsStats',
            'HasBuyClicksCampaigns',
            'BuyClicksCampaign',
          ];
        },
      }),
      unarchiveBuyClicksCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/ClickBuyCampaign/${id}/unarchive/`,
                  method: 'post',
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return [
            'BuyClicksCampaign',
            'BuyCampaignsStats',
            'BuyCampaignsGroupsStats',
            'HasBuyClicksCampaigns',
          ];
        },
      }),
      changeStateBuyClicksCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids, status } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/ClickBuyCampaign/${id}/`,
                  method: 'patch',
                  data: { status },
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return ['BuyClicksCampaign', 'BuyCampaignsStats'];
        },
      }),

      /**
       * Stats
       */
      getBuyClicksCampaignsStats: build.query({
        query: (params = {}) => ({
          url: '/stats/buy_campaign/',
          method: 'get',
          params: {
            product: 'click',
            ordering: '-pk',
            archive_mode: 'all',
            ...params,
          },
        }),
        providesTags: (result) => {
          return !!result?.results?.length
            ? result.results.map(({ buyCampaignId }) => ({
                type: 'BuyCampaignsStats',
                id: buyCampaignId,
              }))
            : ['BuyCampaignsStats'];
        },
      }),
      getBuyClicksCampaignLocations: build.query({
        async queryFn({ id, limit }) {
          try {
            const data = await fetchBuyClicksCampaignLocationsMockData(
              limit,
              id,
            );
            return { data };
          } catch (error) {
            return { error };
          }
        },
        providesTags: () => {
          return ['BuyClicksCampaignLocations'];
        },
      }),
      deleteBuyClicksCampaignLocation: build.mutation({
        async queryFn({ ids, productType }) {
          try {
            await Promise.all(
              ids.map(async (id) => {
                await sleep(200);

                return {
                  id,
                  productType,
                };
              }),
            );

            return { data: [] };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: (result) => {
          return !!result?.rows?.length
            ? result.rows.map(({ id }) => ({
                type: 'BuyClicksCampaignLocations',
                id,
              }))
            : ['BuyClicksCampaignLocations'];
        },
      }),

      /**
       * Utils
       */
      validateClickDeliveryUrl: build.query({
        query: (data) => ({
          url: '/ValidateClickDeliveryUrl/',
          method: 'post',
          data,
        }),
        providesTags: (props) => {
          const { url } = props || {};

          return [{ type: 'DeliveryUrlValid', id: url }];
        },
      }),

      validateClickXmlUrl: build.query({
        query: (data) => ({
          url: '/XMLClickBuyerGatewayUrlValidator/',
          method: 'post',
          data,
        }),
      }),
    };
  },
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetHasBuyClicksCampaignsQuery,
  useGetBuyClicksCampaignQuery,
  useGetBuyClicksCampaignsQuery,
  useCreateBuyClicksCampaignMutation,
  usePutBuyClicksCampaignMutation,
  usePatchBuyClicksCampaignMutation,
  useDeleteBuyClicksCampaignLocationMutation,
  useDeleteBuyClicksCampaignsMutation,
  useArchiveBuyClicksCampaignsMutation,
  useUnarchiveBuyClicksCampaignsMutation,
  useChangeStateBuyClicksCampaignsMutation,
  useGetBuyClicksCampaignsStatsQuery,
  useGetBuyClicksCampaignLocationsQuery,
  useValidateClickDeliveryUrlQuery,
  useLazyValidateClickDeliveryUrlQuery,
  useValidateClickXmlUrlQuery,
  useLazyValidateClickXmlUrlQuery,
} = buyClicksCampaignApi;
